import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import classes from './BoardDice.module.css';

export type DicePhase = 'idle' | 'rolling' | 'landed';

export type DiceRollState = {
  face: number;
  phase: DicePhase;
};

/** A throw has to read as a throw even when the server answers instantly. */
const SPIN_MIN_MS = 900;
/** A rejected roll must not leave the cube spinning forever. */
const SPIN_MAX_MS = 6000;

/**
 * Drives the cube from two independent triggers: the local press, so the throw
 * starts on the click instead of on the response, and the action log, so a
 * throw made by anybody else in the room animates on this screen too.
 */
export function useDiceRoll(value: number | null, seq: number | null) {
  const [state, setState] = useState<DiceRollState>(() => ({ face: value ?? 1, phase: 'idle' }));
  /* Refs, not state: these steer the next transition, they never render. */
  const seenSeq = useRef(seq);
  const thrownAt = useRef(0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  };

  const start = useCallback(() => {
    clearTimers();
    thrownAt.current = Date.now();
    setState((current) => ({ ...current, phase: 'rolling' }));
    timers.current.push(window.setTimeout(() => {
      thrownAt.current = 0;
      setState((current) => ({ ...current, phase: 'idle' }));
    }, SPIN_MAX_MS));
  }, []);

  useEffect(() => {
    if (seq === seenSeq.current) return;
    const isNewRoll = seq !== null && (seenSeq.current === null || seq > seenSeq.current);
    seenSeq.current = seq;
    clearTimers();

    /* Undo rewinds the log rather than adding to it — snap, never re-throw. */
    if (!isNewRoll || value === null) {
      thrownAt.current = 0;
      setState((current) => ({ face: value ?? current.face, phase: 'idle' }));
      return;
    }

    /* Somebody else's throw arrives over the socket with no press behind it. */
    if (thrownAt.current === 0) {
      thrownAt.current = Date.now();
      setState((current) => ({ ...current, phase: 'rolling' }));
    }

    const remaining = Math.max(0, SPIN_MIN_MS - (Date.now() - thrownAt.current));
    timers.current.push(window.setTimeout(() => {
      thrownAt.current = 0;
      setState({ face: value, phase: 'landed' });
    }, remaining));
  }, [seq, value]);

  useEffect(() => clearTimers, []);

  return { face: state.face, phase: state.phase, start };
}

/**
 * Cube geometry, pip layout and the rotation that brings each side to the
 * front — kept in one table so a face can never be described twice.
 *
 * Opposite sides sum to seven, as on a printed die, so the faces a player
 * glimpses mid-tumble are the ones they would expect to see. `cqw` is a length,
 * which `translateZ` demands and percentages cannot give; the wrapper is the
 * query container, so half the side is always `50cqw`.
 *
 * `pips` are [column, row] on the printed three-by-three grid; `rx`/`ry` are the
 * inverse of the side's own rotation.
 */
const FACES = [
  {
    value: 1,
    transform: 'translateZ(50cqw)',
    shade: 0,
    rx: 0,
    ry: 0,
    pips: [[2, 2]],
  },
  {
    value: 6,
    transform: 'rotateY(180deg) translateZ(50cqw)',
    shade: 0.14,
    rx: 0,
    ry: 180,
    pips: [[1, 1], [1, 2], [1, 3], [3, 1], [3, 2], [3, 3]],
  },
  {
    value: 3,
    transform: 'rotateY(90deg) translateZ(50cqw)',
    shade: 0.1,
    rx: 0,
    ry: -90,
    pips: [[1, 1], [2, 2], [3, 3]],
  },
  {
    value: 4,
    transform: 'rotateY(-90deg) translateZ(50cqw)',
    shade: 0.16,
    rx: 0,
    ry: 90,
    pips: [[1, 1], [1, 3], [3, 1], [3, 3]],
  },
  {
    value: 5,
    transform: 'rotateX(90deg) translateZ(50cqw)',
    shade: 0.03,
    rx: -90,
    ry: 0,
    pips: [[1, 1], [1, 3], [2, 2], [3, 1], [3, 3]],
  },
  {
    value: 2,
    transform: 'rotateX(-90deg) translateZ(50cqw)',
    shade: 0.19,
    rx: 90,
    ry: 0,
    pips: [[1, 1], [3, 3]],
  },
] as const;

export function BoardDice({
  face,
  phase,
  visible = true,
}: {
  face: number;
  phase: DicePhase;
  /**
   * The throw is the turn's focus. Off the table the cube fades out in place —
   * and before the first throw of the party it has never been on it, so there is
   * no resting state to design: a die showing a face nobody rolled would read as
   * a result.
   */
  visible?: boolean;
}) {
  /* A value outside 1..6 can only be corrupt state — show the die at rest. */
  const landed = FACES.find((side) => side.value === face) ?? FACES[0];

  return (
    <div
      className={classes.dice}
      data-testid="board-dice"
      data-phase={phase}
      data-visible={visible}
      data-value={landed.value}
      role="img"
      /* Stowed, it is scenery on its way out — not something to announce. */
      aria-hidden={visible ? undefined : true}
      aria-label={`Кубик: выпало ${landed.value}`}
    >
      <span className={classes.shadow} data-phase={phase} aria-hidden="true" />
      <div className={classes.stage}>
        <div className={classes.lift} data-phase={phase}>
          <div
            className={classes.cube}
            data-phase={phase}
            style={{ '--rx': `${landed.rx}deg`, '--ry': `${landed.ry}deg` } as CSSProperties}
          >
            {FACES.map((side) => (
              <div
                key={side.value}
                className={classes.face}
                style={{ transform: side.transform, '--shade': side.shade } as CSSProperties}
              >
                {side.pips.map(([column, row]) => (
                  <span
                    key={`${column}-${row}`}
                    className={classes.pip}
                    style={{ gridColumn: column, gridRow: row }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
